import React, { useState, useEffect, useRef } from "react";
import { useChanges, useEditorData } from "./AdminDashboard";
import { Change } from "./useChangesTracker";
import { fetchSection } from "../../services/api";
import pesquisaData from "../../pesquisa.json";
import { getFieldChanges } from "./deepCompare";
import "./Admin.css";

interface Pesquisa {
  id: number;
  titulo: {
    pt: string;
    en: string;
    es: string;
  };
  url: string;
  data: string;
  autores: string[];
  tipo: string;
  status: string;
}

interface PesquisaEditorProps {
  onEditingChange?: (isEditing: boolean) => void;
}

const PesquisaEditor: React.FC<PesquisaEditorProps> = ({ onEditingChange }) => {
  const { changes: changesList, addChange, clearChange, savedSignal } =
    useChanges();
  const { registerEditor, unregisterEditor } = useEditorData();
  const [items, setItems] = useState<Pesquisa[]>([]);
  const [originalItems, setOriginalItems] = useState<Pesquisa[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState<Pesquisa | null>(null);
  const [activeLang, setActiveLang] = useState<"pt" | "en" | "es">("pt");
  const [sortBy, setSortBy] = useState<"date" | "title" | "tipo">("date");

  useEffect(() => {
    // Load the live content from the worker, falling back to the bundled copy.
    let cancelled = false;
    (async () => {
      const data = await fetchSection<Pesquisa[]>(
        "pesquisa",
        pesquisaData as Pesquisa[]
      );
      if (cancelled) return;
      setItems(data);
      setOriginalItems(data);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Rebaseline after a successful save (no reload -> no stale KV read-back).
  const itemsRef = useRef(items);
  itemsRef.current = items;
  useEffect(() => {
    if (savedSignal > 0) setOriginalItems(itemsRef.current);
  }, [savedSignal]);

  useEffect(() => {
    // Track changes by comparing current state with original
    if (loading) return;

    // Clear all old changes for this section first
    items.forEach((p) => {
      clearChange(`pesquisa-${p.id}-add`);
      clearChange(`pesquisa-${p.id}-edit`);
      clearChange(`pesquisa-${p.id}-delete`);
    });
    originalItems.forEach((p) => {
      clearChange(`pesquisa-${p.id}-add`);
      clearChange(`pesquisa-${p.id}-edit`);
      clearChange(`pesquisa-${p.id}-delete`);
    });

    // Track new changes
    items.forEach((item) => {
      const original = originalItems.find((p) => p.id === item.id);
      if (!original) {
        addChange({
          id: `pesquisa-${item.id}-add`,
          section: "Pesquisa",
          type: "add",
          description: `Adicionar: ${item.titulo.pt || "Nova pesquisa"}`,
        });
      } else {
        const fieldChanges = getFieldChanges(original, item);
        if (fieldChanges.length > 0) {
          addChange({
            id: `pesquisa-${item.id}-edit`,
            section: "Pesquisa",
            type: "edit",
            description: `Editar: ${item.titulo.pt || "Pesquisa sem título"}`,
            fieldChanges,
          });
        }
      }
    });

    // Track deletions (only for items that existed in original data)
    originalItems.forEach((item) => {
      if (!items.find((i) => i.id === item.id)) {
        addChange({
          id: `pesquisa-${item.id}-delete`,
          section: "Pesquisa",
          type: "delete",
          description: `Excluir: ${item.titulo.pt || "Pesquisa sem título"}`,
        });
      }
    });
  }, [items, originalItems, addChange, clearChange, loading]);

  useEffect(() => {
    onEditingChange?.(editingItem !== null);
  }, [editingItem, onEditingChange]);

  // Register data getter for AdminDashboard
  useEffect(() => {
    const getData = () => [{ section: "pesquisa", data: items }];
    registerEditor("Pesquisa", getData);
    return () => unregisterEditor("Pesquisa");
  }, [items, registerEditor, unregisterEditor]);

  const addItem = () => {
    const newItem: Pesquisa = {
      id: Math.max(...items.map((i) => i.id), 0) + 1,
      titulo: { pt: "", en: "", es: "" },
      url: "",
      data: "",
      autores: [],
      tipo: "artigo",
      status: "em_andamento",
    };
    setItems([...items, newItem]);
    setEditingItem(newItem);
  };

  const updateItem = (updatedItem: Pesquisa) => {
    setItems(items.map((i) => (i.id === updatedItem.id ? updatedItem : i)));
    setEditingItem(null);
  };

  const deleteItem = (id: number) => {
    if (window.confirm("Tem certeza que deseja excluir este item?")) {
      // Check if this item existed in original data
      const wasOriginal = originalItems.find((i) => i.id === id);

      setItems(items.filter((i) => i.id !== id));

      // If it was a newly added item (not in original), clear the add change
      if (!wasOriginal) {
        clearChange(`pesquisa-${id}-add`);
        clearChange(`pesquisa-${id}-edit`);
      }
    }
  };

  if (loading) return <div className="loading">Carregando...</div>;

  return (
    <div className="editor-container">
      <div className="editor-header">
        <h2>Gerenciar Pesquisa</h2>
        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          <select
            value={sortBy}
            onChange={(e) =>
              setSortBy(e.target.value as "date" | "title" | "tipo")
            }
            style={{
              padding: "8px 12px",
              borderRadius: "6px",
              border: "1px solid #ddd",
            }}
          >
            <option value="date">Ordenar por Data</option>
            <option value="title">Ordenar por Título</option>
            <option value="tipo">Ordenar por Tipo</option>
          </select>
          <button onClick={addItem} className="add-button">
            Adicionar Item
          </button>
        </div>
      </div>

      <div className="item-list">
        {[...items]
          .sort((a, b) => {
            if (sortBy === "date") {
              return (
                new Date(b.data.split("/").reverse().join("-")).getTime() -
                new Date(a.data.split("/").reverse().join("-")).getTime()
              );
            } else if (sortBy === "title") {
              return a.titulo.pt.localeCompare(b.titulo.pt);
            } else {
              return a.tipo.localeCompare(b.tipo);
            }
          })
          .map((item) => {
            const hasChanges = changesList.some((c: Change) =>
              c.id.includes(`pesquisa-${item.id}`)
            );

            return (
              <div
                key={item.id}
                className={`item-card ${hasChanges ? "item-card-changed" : ""}`}
              >
                <div>
                  <h3>{item.titulo.pt || "Sem título"}</h3>
                  <p>
                    {item.autores.join(", ")} - {item.tipo} - {item.status}
                  </p>
                </div>
                <div className="item-actions">
                  <button
                    onClick={() => setEditingItem(item)}
                    className="edit-button"
                  >
                    Editar
                  </button>
                  <button
                    onClick={() => deleteItem(item.id)}
                    className="delete-button"
                  >
                    Excluir
                  </button>
                </div>
              </div>
            );
          })}
      </div>

      {editingItem && (
        <PesquisaModal
          item={editingItem}
          onClose={() => setEditingItem(null)}
          onSave={updateItem}
          activeLang={activeLang}
          setActiveLang={setActiveLang}
        />
      )}
    </div>
  );
};

interface PesquisaModalProps {
  item: Pesquisa;
  onClose: () => void;
  onSave: (item: Pesquisa) => void;
  activeLang: "pt" | "en" | "es";
  setActiveLang: (lang: "pt" | "en" | "es") => void;
}

const PesquisaModal: React.FC<PesquisaModalProps> = ({
  item,
  onClose,
  onSave,
  activeLang,
  setActiveLang,
}) => {
  const [formData, setFormData] = useState<Pesquisa>(item);

  const handleSave = () => {
    onSave(formData);
  };

  const langs = ["pt", "en", "es"] as const;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>Editar Pesquisa</h2>

        <div className="language-tabs">
          {langs.map((lang) => (
            <button
              key={lang}
              className={`language-tab ${activeLang === lang ? "active" : ""}`}
              onClick={() => setActiveLang(lang)}
            >
              {lang.toUpperCase()}
            </button>
          ))}
        </div>

        <div className="form-group">
          <label>Título ({activeLang.toUpperCase()})</label>
          <input
            type="text"
            value={formData.titulo[activeLang]}
            onChange={(e) =>
              setFormData({
                ...formData,
                titulo: { ...formData.titulo, [activeLang]: e.target.value },
              })
            }
          />
        </div>

        <div className="form-group">
          <label>URL</label>
          <input
            type="url"
            value={formData.url}
            onChange={(e) => setFormData({ ...formData, url: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label>Data (DD/MM/YYYY)</label>
          <input
            type="text"
            value={formData.data}
            onChange={(e) => setFormData({ ...formData, data: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label>Autores (separados por vírgula)</label>
          <input
            type="text"
            value={formData.autores.join(", ")}
            onChange={(e) =>
              setFormData({
                ...formData,
                autores: e.target.value.split(",").map((a) => a.trim()),
              })
            }
          />
        </div>

        <div className="form-group">
          <label>Tipo</label>
          <select
            value={formData.tipo}
            onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
          >
            <option value="artigo">Artigo</option>
            <option value="tese">Tese</option>
            <option value="dissertacao">Dissertação</option>
            <option value="livro">Livro</option>
            <option value="capitulo">Capítulo de Livro</option>
            <option value="conferencia">Conferência</option>
            <option value="relatorio">Relatório Técnico</option>
          </select>
        </div>

        <div className="form-group">
          <label>Status</label>
          <select
            value={formData.status}
            onChange={(e) =>
              setFormData({ ...formData, status: e.target.value })
            }
          >
            <option value="concluido">Concluído</option>
            <option value="em_andamento">Em Andamento</option>
          </select>
        </div>

        <div className="form-actions">
          <button onClick={onClose} className="cancel-button">
            Cancelar
          </button>
          <button onClick={handleSave} className="save-button">
            Salvar
          </button>
        </div>
      </div>
    </div>
  );
};

export default PesquisaEditor;
